import { useEffect, useRef, type ReactNode } from 'react'
import { Modal } from '../components/Modal.js'
import { Icon, type IconName } from '../components/Icon.js'
import { Button } from '../components/ui.js'
import { Kbd } from './Markup.js'

/**
 * The first thing a new person sees, once. Decision 115.
 *
 * Three answers, each one honest about what it does: show me round now, ask
 * me another time, or never ask again (Settings brings it back). Closing it
 * any other way — Escape, the scrim — is "Not now", because nobody turns a
 * feature off for good by accident.
 */

const DESK_POINTS: Array<{ icon: IconName; title: string; body: ReactNode }> = [
  {
    icon: 'schedule',
    title: 'The screens you will use',
    body: 'Today, Jobs, the Board, Billing — what each is for and where it lives.',
  },
  {
    icon: 'jobs',
    title: 'A job opens over any screen',
    body: 'Click a job anywhere and its card opens on top, so you never lose your place.',
  },
  {
    icon: 'help',
    title: 'Help is one key away',
    body: (
      <>
        Press <Kbd>?</Kbd> on any screen for the guides to what you are looking at.
      </>
    ),
  },
]

const FIELD_POINTS: Array<{ icon: IconName; title: string; body: ReactNode }> = [
  {
    icon: 'today',
    title: 'Your day, next job first',
    body: 'Call, and the button that moves the visit on, right at the top.',
  },
  {
    icon: 'camera',
    title: 'A job on site',
    body: 'Notes, photos and the report, kept on the phone until they are sent.',
  },
  {
    icon: 'help',
    title: 'Help in Me',
    body: 'The guides, this tour again, and what changed in each version.',
  },
]

export function Welcome({
  name,
  field,
  onStart,
  onLater,
  onNever,
}: {
  name: string
  field: boolean
  onStart: () => void
  onLater: () => void
  onNever: () => void
}) {
  const start = useRef<HTMLButtonElement>(null)

  // After the dialog has taken focus for itself, so Enter starts the tour.
  useEffect(() => {
    const frame = requestAnimationFrame(() => start.current?.focus())
    return () => cancelAnimationFrame(frame)
  }, [])

  const first = name.split(/\s+/)[0] ?? name
  const points = field ? FIELD_POINTS : DESK_POINTS

  return (
    <Modal open onClose={onLater} labelledBy="guide-welcome-title" className="sm:max-w-[520px]">
      <div className="relative overflow-hidden bg-ink-900 px-5 pt-5 pb-5 text-white sm:px-6">
        <img
          src="/brand/ecopest-mark-colour-on-dark.svg"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -top-4 -right-6 h-36 w-auto opacity-[0.08]"
        />
        <img src="/brand/ecopest-mark-colour-on-dark.svg" alt="EcoPest" className="h-8 w-auto" />
        <h2
          id="guide-welcome-title"
          className="mt-4 font-display text-[22px] leading-tight font-extrabold"
        >
          Welcome to EcoPest, {first}
        </h2>
        <p className="mt-1 text-[13.5px] font-medium text-brand-300">
          A short tour of the {field ? 'app' : 'screens you will use'}. About a minute.
        </p>
      </div>

      <ul className="flex flex-col gap-3.5 px-5 py-5 sm:px-6">
        {points.map((point) => (
          <li key={point.title} className="flex items-start gap-3">
            <span className="grid size-9 shrink-0 place-items-center rounded-md bg-brand-100 text-brand-700">
              <Icon name={point.icon} />
            </span>
            <div className="min-w-0 pt-0.5">
              <p className="text-sm font-semibold text-ink-900">{point.title}</p>
              <p className="text-[13px] leading-snug text-ink-500">{point.body}</p>
            </div>
          </li>
        ))}
      </ul>

      <div className="flex flex-col-reverse gap-2 border-t border-ink-100 bg-ink-50/60 px-5 py-3 sm:flex-row sm:items-center sm:px-6">
        <button
          type="button"
          onClick={onNever}
          className="rounded-md px-1 py-1.5 text-xs font-medium text-ink-500 hover:text-ink-900 sm:mr-auto"
        >
          Don’t show this again
        </button>
        <Button variant="ghost" onClick={onLater}>
          Not now
        </Button>
        <Button ref={start} variant="primary" onClick={onStart}>
          Show me around
          <Icon name="forward" className="size-4" />
        </Button>
      </div>
    </Modal>
  )
}
